import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AccountIcon, {
  AccountIconProps,
} from 'shared/components/vxl/AccountIcon/AccountIcon';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

import AccountAction, {
  AccountActionsContainerProps,
} from './AccountActionList/AccountActionList';
import AmountSection from './base/AmountSection';

export interface HorizontalAccountCardProps extends PressableAnalyticsProps {
  accountIconProps: AccountIconProps;
  titleText: string;
  titleAccessory?: Maybe<string | TagProps>;
  amountText?: string;
  onAddIconClick?: () => void;
  isAmountStale?: boolean;
  accountActions?: AccountActionsContainerProps;
  disabled?: boolean;
  onCardClick?: () => void;
}

const HorizontalAccountCard = ({
  accountIconProps,
  titleText,
  titleAccessory,
  amountText,
  onAddIconClick,
  isAmountStale = false,
  accountActions,
  disabled,
  onCardClick,
  analyticsId = undefined,
  customAttributes = null,
}: HorizontalAccountCardProps) => {
  const titleAccessoryLineLimit = 0;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const buttonAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  return (
    <View style={styles.container}>
      <Pressable
        onPress={() => {
          buttonAnalytics();
          if (onCardClick) {
            onCardClick();
          }
        }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        style={[
          styles.interactiveContainer,
          accountActions && styles.borderRadius,
          isFocused && interactiveStyles.focus,
          isHovered && interactiveStyles.hover,
          isPressed && interactiveStyles.press,
        ]}
      >
        <View style={styles.infoSection}>
          <View style={styles.accountIconWrapper}>
            <AccountIcon {...accountIconProps} />
          </View>
          <View style={styles.info}>
            <BodySmText
              numberOfLines={1}
              ellipsizeMode="tail"
              style={disabled && interactiveStyles.textDisable}
            >
              {titleText}
            </BodySmText>

            {titleAccessory ? (
              <View>
                {typeof titleAccessory === 'string' ? (
                  <BodyXSText
                    numberOfLines={titleAccessoryLineLimit}
                    style={[
                      styles.secondaryText,
                      disabled && interactiveStyles.textDisable,
                    ]}
                  >
                    {titleAccessory}
                  </BodyXSText>
                ) : (
                  <Tag text={titleAccessory.text} type={titleAccessory.type} />
                )}
              </View>
            ) : null}
          </View>
        </View>
        <View style={styles.amountSection}>
          <AmountSection
            amountText={amountText}
            onAddIconClick={onAddIconClick}
            isAmountStale={isAmountStale}
            disabled={disabled}
          />
        </View>
      </Pressable>
      {accountActions ? (
        <AccountAction {...(accountActions as AccountActionsContainerProps)} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  accountIconWrapper: {
    alignItems: 'center',
    backgroundColor: theme.color.background.tertiary,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  amountSection: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'flex-end',
  },
  borderRadius: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  container: {
    width: '100%',
  },
  info: {
    flex: 1,
    gap: theme.spacing.xs,
    justifyContent: 'center',
  },
  infoSection: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.s,
  },
  interactiveContainer: {
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 12,
    width: '100%',
  },
  secondaryText: {
    color: theme.color.text.secondary,
  },
});

const interactiveStyles = StyleSheet.create({
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  textDisable: {
    color: theme.color.interactive.disabled.text,
  },
});

export default HorizontalAccountCard;
